import {
  ITranslationsListViewModel,
  ITranslationsListLocalFilters,
} from "./__types__/ITranslationsListViewModel";
import { Pagination } from "../pagination";
import { IPagination, IPaginationMeta } from "../pagination/types";
import { Statuses } from "../statuses";
import { RequestStatus } from "@constants/repositories";
import { isCancel } from "axios";
import { IRootTreeModel } from "@models/RootTreeModel";
import { observable } from "mobx";
import { AxiosRequestClient, Utils } from "@modules/index";
import { TranslatesRepository } from "@repositories/translates";
import { ITranslatesRepository } from "@repositories/translates/types";
import { ITranslateEntity, TranslateEntity } from "@entities/TranslateEntity";

export class TranslationsListViewModel implements ITranslationsListViewModel {
  public statuses: Statuses = new Statuses(["fetchItems"]);

  // Observables
  public _localFilter: ITranslationsListLocalFilters = observable({
    per_page: 1000000000,
    search: "",
    segment: "api",
    sort_field: "",
    sort_order: "",
    sorting_direction: "desc",
  });

  // Repository
  private TranslatesRepository: ITranslatesRepository =
    new TranslatesRepository(new AxiosRequestClient(this.model, {}));

  // Pagination
  private _pagination: IPagination<ITranslateEntity> = new Pagination(
    undefined
  );

  /**
   * Constructor for the `RegionsListViewModel` class.
   *
   * @param model - The root tree model.
   */
  public constructor(private model: IRootTreeModel) {}

  /**
   * Get the list of filters applied to the request.
   *
   * @returns The filters object.
   */
  public get filters(): ITranslationsListLocalFilters {
    return {
      per_page: this._localFilter.per_page,
      search: this._localFilter.search,
      segment: this._localFilter.segment,
      sort_field: this._localFilter.sort_field,
      sort_order: this._localFilter.sort_order,
      sorting_direction: this._localFilter.sorting_direction,
    };
  }

  /**
   * Get the meta data of the pagination.
   *
   * @returns The pagination meta data.
   */
  public get meta(): IPaginationMeta {
    return this._pagination.meta;
  }

  /**
   * Get the metadata of the pagination.
   *
   * @returns The pagination metadata.
   */
  public get metadata(): IPagination<ITranslateEntity> {
    return this._pagination;
  }

  /**
   * Get the list of regions.
   *
   * @returns The list of regions.
   */
  public get list(): ITranslateEntity[] {
    return this._pagination.data;
  }

  /**
   * Get the list of translations.
   *
   * @returns The list of translations.
   */
  public get listFiltered(): ITranslateEntity[] {
    return this._pagination.data.filter(
      (translation) =>
        Utils.findSubstring(translation.key, this._localFilter.search) ||
        Utils.findSubstring(
          translation.translations.ar,
          this._localFilter.search
        ) ||
        Utils.findSubstring(
          translation.translations.en,
          this._localFilter.search
        )
    );
  }

  /**
   * Set the search filter.
   *
   * @param search - The search keyword.
   */
  public setFilterSearch = (search: string): void => {
    this._localFilter.search = search;
  };

  /**
   * Set the filter by segment.
   *
   * @param segment - The filtered keyword.
   */
  public setFilterBySegment = (
    segment: ITranslationsListLocalFilters["segment"]
  ): void => {
    this._localFilter.segment = segment;
    this.fetchItemsBatch(0, true);
  };

  /**
   * Set the sort field.
   *
   * @param field - The sort field name.
   */
  public setSortField = (field: string): void => {
    this._localFilter.sort_field = field;
  };

  /**
   * Set the sort order.
   *
   * @param order - The field name.
   */
  public setSortOrder = (order: string): void => {
    this._localFilter.sort_order = order;
  };

  /**
   * Fetch a batch of items.
   *
   * @param page - The page number.
   * @param refreshing - True if the page is being refreshed, false otherwise.
   */
  public fetchItemsBatch = async (
    page: number = this._pagination.meta.current_page + 1,
    refreshing: boolean
  ): Promise<void> => {
    try {
      // Check if previous request is ongoing
      if (
        !refreshing &&
        this.statuses.getStatus("fetchItems") === RequestStatus.Pending
      ) {
        console.warn("Could not make request until previous not finished");
        return;
      }

      // Check if maximum number of pages already loaded
      if (
        !refreshing &&
        this._pagination.meta.current_page >= this._pagination.meta.last_page
      ) {
        console.warn(
          "Could not make request because maximum count of pages already loaded"
        );
        return;
      }

      // Set status to pending
      this.statuses.setStatus("fetchItems", RequestStatus.Pending);

      const response = await this.TranslatesRepository.fetchTechnicalTranslates(
        {
          per_page: 1000000000,
          segment: this._localFilter.segment,
          sorting_direction: "desc",
        }
      );

      // Update pagination data
      this._pagination.metadata = {
        data: response.data.map(
          (item): ITranslateEntity => TranslateEntity.create(item)
        ),
        meta: {
          current_page: 1,
          last_page: 1,
          per_page: this.filters.per_page,
          total: response.data.length,
        },
      };

      // Set status to success
      this.statuses.setStatus("fetchItems", RequestStatus.Success);
    } catch (error) {
      // Check if request was cancelled
      if (isCancel(error)) {
        return;
      }

      // Set status to error
      this.statuses.setStatus("fetchItems", RequestStatus.Error);
      throw error;
    }
  };

  /**
   * Clear the list of regions.
   */
  public clearList = (): void => {
    this._pagination.metadata = {};
  };

  /**
   * Clean up resources before destroying the view model.
   */
  public beforeDestroy(): void {
    this.TranslatesRepository.beforeDestroy();
  }
}
